/**
 * Petición y detección del permiso de ubicación (porteado de Tactic, 2026-08-04).
 *
 * ⚠️ No uses `navigator.permissions.query({name:"geolocation"})` como fuente de
 * verdad. Safari / WKWebView (o sea, TODOS los iPhone) no lo implementa para
 * geolocalización: con el permiso DENEGADO sigue respondiendo `"prompt"`. La
 * única señal fiable en ambas plataformas es el código de error de
 * `getCurrentPosition` (1 = denegado).
 *
 * ⚠️ Trampa de Android: si el trabajador elige "Aproximada" en el diálogo del
 * sistema, la petición de la WebView falla como PERMISSION_DENIED. El texto de
 * ayuda debe nombrar "ubicación precisa" explícitamente.
 */
import { canUseNativeLocation, getDespiaFix } from "@/lib/geo/despiaLocation";

export type LocationPermissionState =
  | "granted"
  | "denied"
  | "unavailable"
  | "unsupported"
  | "unknown";

export type MobileOS = "ios" | "android" | "other";

export function detectMobileOS(): MobileOS {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  // iPadOS 13+ se anuncia como Macintosh; se distingue por el táctil.
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Macintosh/i.test(ua) && typeof document !== "undefined" && "ontouchend" in document) {
    return "ios";
  }
  return "other";
}

function probe(options: PositionOptions): Promise<LocationPermissionState> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      resolve("unsupported");
      return;
    }
    let settled = false;
    const done = (state: LocationPermissionState) => {
      if (settled) return;
      settled = true;
      resolve(state);
    };
    try {
      navigator.geolocation.getCurrentPosition(
        () => done("granted"),
        (err) => done(err.code === err.PERMISSION_DENIED ? "denied" : "unavailable"),
        options,
      );
    } catch {
      // Algunos WebView lanzan si el permiso está revocado a nivel de sistema.
      done("denied");
    }
  });
}

/**
 * Pide el permiso de verdad — dispara el diálogo del sistema. En la app nativa
 * se intenta PRIMERO por el puente de Despia (`location://simple`), que sí
 * solicita el permiso "Al usar la app" desde la capa nativa. Si el puente
 * devuelve posición, el permiso está concedido; si no, se cae a la API web,
 * la única que distingue DENEGADO de "sin fix ahora".
 */
export async function requestLocationPermission(): Promise<LocationPermissionState> {
  if (canUseNativeLocation()) {
    const nativeFix = await getDespiaFix();
    if (nativeFix) return "granted";
  }
  return probe({ enableHighAccuracy: true, maximumAge: 0, timeout: 15_000 });
}
